import React from "react";

// Replaces window.confirm() for the "resume saved workflow?" prompt.
// Native confirm() dialogs get silently auto-suppressed by the browser after
// repeated calls in the same tab (Chrome/Edge return `false` with no dialog
// shown at all once that kicks in), which was causing stale formData
// (including an old data_upload_id) to get silently reloaded. A real modal
// can't be suppressed that way, and always requires an explicit click.
const ResumeWorkflowModal = ({ open, onResume, onStartNew }) => {
  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      style={{
        background:
          "radial-gradient(circle at 50% 40%, rgba(3,43,78,0.75) 0%, rgba(2,26,48,0.9) 100%)",
        backdropFilter: "blur(6px)",
      }}
    >
      <style>{`
        @keyframes resumeModalPopIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.94); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes resumeModalGlow {
          0%, 100% { box-shadow: 0 0 0 8px rgba(184,132,47,0.10); }
          50% { box-shadow: 0 0 0 14px rgba(184,132,47,0.16); }
        }
      `}</style>

      <div
        className="relative w-full max-w-[440px] rounded-[28px] bg-white overflow-hidden"
        style={{
          fontFamily: "'Inter', sans-serif",
          boxShadow:
            "0 24px 60px rgba(2,26,48,0.45), 0 2px 8px rgba(2,26,48,0.15)",
          animation: "resumeModalPopIn 0.28s cubic-bezier(0.34,1.56,0.64,1) both",
        }}
      >
        {/* Top accent band */}
        <div
          style={{
            height: 8,
            background: "linear-gradient(90deg, #032B4E 0%, #1F4280 45%, #c79838 100%)",
          }}
        />

        <div className="flex flex-col items-center text-center px-8 pt-8 pb-6">
          {/* Icon badge */}
          <div
            className="flex items-center justify-center rounded-full mb-5"
            style={{
              width: 68,
              height: 68,
              background: "linear-gradient(135deg, #1F4280 0%, #032B4E 100%)",
              animation: "resumeModalGlow 2.2s ease-in-out infinite",
            }}
          >
            <iconify-icon
              icon="mdi:history"
              width="34"
              height="34"
              style={{ color: "#f4d99b" }}
            />
          </div>

          <h2
            className="text-[22px] text-[#0F1116] mb-2"
            style={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700 }}
          >
            Resume Your Workflow?
          </h2>

          <p
            className="text-[14px] text-[#5b6474] leading-relaxed max-w-[320px]"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            We found a saved, in-progress workflow. Pick up right where you
            left off, or discard it and start fresh.
          </p>
        </div>

        <div className="flex flex-col gap-3 px-8 pb-8">
          <button
            type="button"
            onClick={onResume}
            className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-2xl text-white font-semibold text-[15px] transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg"
            style={{
              background: "linear-gradient(135deg, #c79838 0%, #b8842f 100%)",
              boxShadow: "0 8px 20px rgba(184,132,47,0.35)",
            }}
          >
            <iconify-icon icon="mdi:play-circle" width="20" height="20" />
            Resume Saved Workflow
          </button>

          <button
            type="button"
            onClick={onStartNew}
            className="flex items-center justify-center gap-2 w-full px-5 py-3.5 rounded-2xl font-semibold text-[15px] border-2 border-[#e2e6ee] text-[#032B4E] transition-all duration-200 hover:bg-[#f5f8fc] hover:border-[#1F4280]"
          >
            <iconify-icon icon="mdi:file-plus-outline" width="20" height="20" />
            Start a New Workflow
          </button>

          <div
            className="flex items-center justify-center gap-1.5 text-[12px] text-[#9aa3b2] mt-1"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <iconify-icon icon="mdi:information-outline" width="14" height="14" />
            Starting new permanently discards the saved draft
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeWorkflowModal;
